import type { CallListItem, CallMedia } from "./types";

/** Recall can take about an hour to finish the recording after the call ends. */
export const MEDIA_WAIT_MS = 2 * 60 * 60 * 1000;

/** A call this recent is shown as processing before the file has been checked. */
export const FRESH_CALL_MS = 15 * 60 * 1000;

/** How often a still-rendering recording is asked again. */
export const MEDIA_POLL_MS = 15_000;

/** How often a write-up still being written is asked for again, and how many times. */
export const WRITEUP_POLL_MS = 3000;
export const WRITEUP_POLL_TRIES = 60;

/** How often the open list looks for a call that just finished. */
export const LIST_POLL_MS = 3_000;

export function callEndedMs(call: CallListItem): number | null {
  if (call.ended_at) {
    const ended = Date.parse(call.ended_at);
    if (!Number.isNaN(ended)) return ended;
  }
  const started = Date.parse(call.created_at);
  if (Number.isNaN(started)) return null;
  return started + call.duration_seconds * 1000;
}

function ageMs(call: CallListItem, now: number): number | null {
  const ended = callEndedMs(call);
  if (ended == null) return null;
  return now - ended;
}

/** How long to keep asking for the file. A long recording can still be rendering
 * after the usual two hours, so the watch lasts at least as long as the call. */
export function processingWindowMs(call: CallListItem): number {
  const length = Number.isFinite(call.duration_seconds) ? Math.max(0, call.duration_seconds) * 1000 : 0;
  return Math.max(MEDIA_WAIT_MS, length);
}

/** A finished call whose file may still be rendering. Older calls stay ordinary rows. */
export function mayStillBeProcessing(call: CallListItem, now = Date.now()): boolean {
  const age = ageMs(call, now);
  if (age == null) return false;
  return age < processingWindowMs(call) && age > -60_000;
}

/**
 * The recording this row is waiting on. A meeting is not finished when only
 * the audio file has landed: the video is archived later, and the list was
 * treating that audio as the finished call.
 */
export function recordingReady(call: CallListItem, media: CallMedia): boolean {
  if (media.kind === "none") return true;
  if (call.bot_type === "DESKTOP") return media.kind === "audio";
  return media.kind === "video";
}

/** The meeting they just had. Shown as processing before its file is checked. */
export function isFreshCall(call: CallListItem, now = Date.now()): boolean {
  const age = ageMs(call, now);
  if (age == null) return false;
  return age < FRESH_CALL_MS && age > -60_000;
}

/**
 * A quiet reload of the first page. Names already filled in stay, and calls
 * the reader has paged to stay under the new first page.
 */
export function mergeFirstPage(current: CallListItem[], fresh: CallListItem[]): CallListItem[] {
  const byId = new Map(current.map((call) => [call.bot_id, call]));
  const merged = fresh.map((call) => {
    const previous = byId.get(call.bot_id);
    if (!previous || call.attendees != null || previous.attendees == null) return call;
    return { ...call, attendees: previous.attendees };
  });
  const freshIds = new Set(fresh.map((call) => call.bot_id));
  const older = current.filter((call) => !freshIds.has(call.bot_id));
  return [...merged, ...older];
}
